import type { ReactNode } from 'react';

/**
 * The one selection toolbar — CLM-69 (D-69-1…D-69-4).
 *
 * Gerry, 11 Sep 2026, asked for Select all and Clear on every checkbox
 * selection in the system, standardised as one control. Every
 * multi-row checkbox table renders this above the table (a sibling, never a
 * `DataTable` slot — D-69-2), and grouped tables repeat it in the `sm` variant
 * inside each group header with `scope` set.
 *
 * Presentational and **controlled**: it never owns the selection. The surface
 * holds the set (the planner store for Bulk ingest, `edits[row_id].include` for
 * the bundle upload, `useSelection` for a surface whose selection is plain local
 * state) and passes `count` / `total` plus the two callbacks.
 *
 * Vocabulary is fixed (D-69-1): the visible labels are exactly `Select all`,
 * `Clear` and `n of m selected`; the accessible names are
 * `Select all <noun>[ for <scope>]` and `Clear the selection[ for <scope>]`.
 *
 * The counter is a `role="status"` live region so a Select all announces the new
 * count once. A **scoped** counter is named `Selection for <scope>` and keeps
 * class `mut` in the `sm` variant, so a surface has at most one *unnamed*
 * status — the top-level one — and `getByRole('status', { name: '' })` stays
 * unambiguous.
 *
 * Edge inertness (D-69-4): Select all is disabled at n = m, Clear at n = 0 —
 * the native `disabled` attribute, like every other `.btn` on the site — or
 * (CLM-122, R-122-15) when the surface says Select all has nothing left it may
 * tick (`selectAllDisabled`), for a table some of whose rows no Select all reaches.
 */
export interface SelectionToolbarProps {
  /** Accessible name of the toolbar itself, e.g. `Approve and execute`. */
  readonly label: string;
  readonly count: number;
  readonly total: number;
  /** Mirrors the surface's busy gate — the toolbar can never tick while the checkboxes cannot. */
  readonly disabled?: boolean;
  /**
   * CLM-122 (R-122-15) — OR-ed into Select all's `disabled`: the surface says
   * Select all has nothing left it may tick, though `count < total` (rows no
   * Select all reaches). Omitted, D-69-4's `count === total` rule alone applies.
   * Clear is unaffected.
   */
  readonly selectAllDisabled?: boolean;
  readonly onSelectAll: () => void;
  readonly onClear: () => void;
  /** Plural noun carried by the accessible names; defaults to `rows`. */
  readonly noun?: string;
  /** Group scoping — names the counter and the buttons `… for <scope>`. */
  readonly scope?: string;
  /** `sm` is the group-header variant: no margin, counter in the `mut` size. */
  readonly size?: 'md' | 'sm';
  /** The surface's own primary action / inline confirm, right-aligned after the buttons. */
  readonly children?: ReactNode;
}

export function SelectionToolbar({
  label,
  count,
  total,
  disabled = false,
  selectAllDisabled = false,
  onSelectAll,
  onClear,
  noun = 'rows',
  scope,
  size = 'md',
  children,
}: SelectionToolbarProps) {
  const small = size === 'sm';
  return (
    <div className={small ? 'selbar sm' : 'selbar'} role="toolbar" aria-label={label}>
      <span
        className={small ? 'counter mut' : 'counter'}
        role="status"
        aria-live="polite"
        aria-label={scope === undefined ? undefined : `Selection for ${scope}`}
      >
        {count.toLocaleString()} of {total.toLocaleString()} selected
      </span>
      <span className="ctl">
        <button
          type="button"
          className="btn sm"
          disabled={disabled || selectAllDisabled || count === total}
          aria-label={scope === undefined ? `Select all ${noun}` : `Select all ${noun} for ${scope}`}
          onClick={onSelectAll}
        >
          Select all
        </button>
        <button
          type="button"
          className="btn sm qt"
          disabled={disabled || count === 0}
          aria-label={
            scope === undefined ? 'Clear the selection' : `Clear the selection for ${scope}`
          }
          onClick={onClear}
        >
          Clear
        </button>
        {children}
      </span>
    </div>
  );
}
